/**
 * Meta Pixel — central helper with dedupe protection.
 *
 * - Library load is deferred from `public/index.html` (first interaction OR
 *   `window.load` + idle). This file is the only thing app code talks to.
 * - `fbq` is provided by the stub in `index.html` as soon as the page loads,
 *   so calls made before the real library arrives are queued safely by Meta's
 *   own snippet — no need to wrap in setTimeout / Promise.
 * - Duplicate guard: keyed by `eventName::source::pathname` and reset to
 *   accept the same combo only after 800ms. This prevents React StrictMode
 *   double-mounts in dev, fast double-clicks, and rerenders from firing twice
 *   while still allowing repeat conversions in legitimate cases (e.g. user
 *   submits the brochure form twice intentionally on different pages).
 */

const PIXEL_ID = "1511607003794287";
const DEDUPE_WINDOW_MS = 800;
const _recent = new Map(); // key -> timestamp

function _shouldFire(key) {
  const now = Date.now();
  const last = _recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  _recent.set(key, now);
  // periodic cleanup
  if (_recent.size > 200) {
    const cutoff = now - 10_000;
    for (const [k, t] of _recent) if (t < cutoff) _recent.delete(k);
  }
  return true;
}

function _fbq(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq(...args);
  } catch (_) {
    /* swallow — Pixel must never throw into app code */
  }
}

/** Read a cookie by name (returns null if missing or SSR). */
export function getCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Convenience: return both _fbp and _fbc for the current browser. */
export function getFbCookies() {
  return { fbp: getCookie("_fbp"), fbc: getCookie("_fbc") };
}

/** Fire PageView. Called on initial load and every SPA route change. */
export function trackPageView() {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (!_shouldFire(`PageView::${path}`)) return;
  _fbq("track", "PageView");
}

/**
 * Fire Lead. Call ONLY after a successful (HTTP 200) form submission.
 * `source` is a short string for the conversion's content_name, e.g.
 * "contact-form" or "brochure-download".
 * `eventId` (optional but recommended) is the server-supplied event_id that
 * Meta uses to dedupe this browser event against the matching CAPI event.
 */
export function trackLead(source = "unknown", eventId = null) {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (!_shouldFire(`Lead::${source}::${path}::${eventId || ""}`)) return;
  if (eventId) {
    _fbq("track", "Lead", { content_name: source }, { eventID: eventId });
  } else {
    _fbq("track", "Lead", { content_name: source });
  }
}

/**
 * Fire Contact. Call on every WhatsApp CTA click.
 * `source` describes which CTA fired it.
 */
export function trackContact(source = "whatsapp") {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (!_shouldFire(`Contact::${source}::${path}`)) return;
  _fbq("track", "Contact", { content_name: source });
}

export const META_PIXEL_ID = PIXEL_ID;
